import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Card,
  Divider,
  Avatar,
  LinearProgress,
  Tooltip,
  Typography,
  Modal,
  IconButton,
} from "@mui/material";
import { Edit, Delete, Search } from "@mui/icons-material";
import ApiEndpoints from "../../network/ApiEndPoints";
import ApiPaginateSearch from "../../component/ApiPaginateSearch";
import { dateToTime, dateToTime1, ddmmyy } from "../../utils/DateUtils";
import AuthContext from "../../store/AuthContext";
import { CustomStyles } from "../../component/CustomStyle";
import CachedIcon from "@mui/icons-material/Cached";
import FilterCard from "../../modals/FilterCard";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { postJsonData, get } from "../../network/ApiController";
import { okSuccessToast, apiErrorToast } from "../../utils/ToastUtil";

import DataFilter from "./Common/DataFilter";
import ViewModal from "../../modals/ViewModal";
import FilterModal from "../../modals/FilterModal";
import { useMediaQuery, useTheme } from "@mui/material";
import CreateAssetModal from "./CreateAssetModal";
import EditAssetModal from "./EditAssetModal";
import ProgressBar from "../../component/ProgressBar";
import CreateProject from "./CreateProject";
import EditProject from "../../component/EditProject";
import DeleteProjectModel from "./DeleteProjectModel";

const TableProjects = ({}) => {
  const [apiData, setApiData] = useState([]);
  const [query, setQuery] = useState("");
  const authCtx = useContext(AuthContext);
  const user = authCtx?.user;
  const [search, setSearch] = useState("");
  const [hiddenButton, setHiddenButton] = useState("");
  const [request, setRequest] = useState(false);
  const [users, setUsers] = useState({}); // Store user ID -> Name mapping
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const refreshRef = useRef(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState(null);

  const refreshFunc = () => {
    refreshRef.current && refreshRef.current();
  };
  const handleDeleteClick = (row) => {
    setDeleteData(row);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteData) return;

    try {
      setRequest(true);
      await postJsonData(
        `${ApiEndpoints.DELETE_PROJECT}/${deleteData.id}`,
        {}, // Assuming POST method with empty payload, adjust if DELETE
        setRequest,
        () => {
          okSuccessToast("Project deleted successfully");
          setOpenDeleteModal(false);
          refreshFunc();
        },
        (error) => {
          apiErrorToast(error);
        }
      );
    } catch (error) {
      apiErrorToast(error);
    } finally {
      setRequest(false);
    }
  };

  const columns = [
    {
      name: "Created / Updated",
      width: "160px",
      wrap: false,
      center: true,
      selector: (row) => (
        <div style={{ textAlign: "center" }}>
          <Tooltip title="Created At">
            <div>
              <span>{ddmmyy(row.created_at)}</span>
              <span style={{ marginLeft: 6 }}>
                {dateToTime1(row.created_at)}
              </span>
            </div>
          </Tooltip>

          <Tooltip title="Updated At">
            <div>
              <span>{ddmmyy(row.updated_at)}</span>
              <span style={{ marginLeft: 6 }}>
                {dateToTime1(row.updated_at)}
              </span>
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      name: "Start / End",
      width: "160px",
      wrap: false,
      center: true,
      selector: (row) => (
        <div style={{ textAlign: "center" }}>
          <Tooltip title="Start date At">
            <div>
              <span>{ddmmyy(row.start_date)}</span>
              <span style={{ marginLeft: 6 }}>
                {dateToTime1(row.start_date)}
              </span>
            </div>
          </Tooltip>

          <Tooltip title="End Date At">
            <div>
              <span>{ddmmyy(row.end_date)}</span>
              <span style={{ marginLeft: 6 }}>{dateToTime1(row.end_date)}</span>
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      name: "Project Image",
      width: "130px",
      wrap: false,
      center: true,
      selector: (row) => (
        <img
          src={row.project_image}
          alt="project"
          onClick={() => {
            setSelectedImage(row.project_image);
            setOpenImageModal(true);
          }}
          style={{
            width: 90,
            height: 40,
            borderRadius: 8,
            cursor: "pointer",
          }}
        />
      ),
    },

    {
      name: "Project Name",
      width: "160px",
      wrap: false,
      center: true,
      selector: (row) => row.name,
    },
    {
      name: "Technologies",
      width: "160px",
      wrap: false,
      center: true,
      color: "green",
      selector: (row) => row.technologies,
    },
    {
      name: "Budget",
      width: "120px",
      wrap: false,
      center: true,
      selector: (row) => `₹ ${Number(row.budget).toFixed(2)}`,
    },

    {
      name: "Priority",
      width: "100px",
      wrap: false,
      center: true,
      selector: (row) => {
        const getColor = (priority) => {
          switch (priority?.toLowerCase()) {
            case "high":
              return "red";
            case "medium":
              return "#ff9800";
            case "low":
              return "green";
            default:
              return "#333";
          }
        };

        return (
          <span
            style={{
              fontWeight: 600,
              color: getColor(row.priority),
            }}
          >
            {row.priority}
          </span>
        );
      },
    },

    {
      name: "Total Tasks",
      width: "120px",
      wrap: false,
      center: true,
      selector: (row) => row.tasksCount || 0,
    },

    {
      name: "Pending Tasks",
      width: "140px",
      wrap: false,
      center: true,
      selector: (row) =>
        row.tasksCount ? row.tasksCount - row.completeCount : 0,
    },

    {
      name: "Completed Tasks",
      width: "150px",
      wrap: false,
      center: true,
      selector: (row) => row.completeCount || 0,
    },
    ...(user?.role === "Admin"
      ? [
          {
            name: "Status",
            width: "100px",
            wrap: false,
            center: true,
            selector: (row) => row.status || "NA",
          },
        ]
      : []),
    {
      name: "Task Progress",
      width: "200px",
      wrap: false,
      center: true,
      selector: (row) => {
        const percentage =
          row.tasksCount > 0
            ? ((row.completeCount / row.tasksCount) * 100).toFixed(2)
            : 0;

        return (
          <div style={{ width: "180px", textAlign: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {row.tasksCount > 0 ? `Progress: ${percentage}%` : "No tasks"}
            </Typography>

            <LinearProgress
              variant="determinate"
              value={parseFloat(percentage)}
              sx={{
                mt: 1,
                height: 8,
                borderRadius: 4,
              }}
            />
          </div>
        );
      },
    },

    ...(user?.role === "Admin"
      ? [
          {
            name: "Action",
            width: "120px",
            center: true,
            omit: user?.role === "Admin",
            selector: (row) => (
              <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                <Tooltip title="Edit Project">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      setEditData(row);
                      setOpenEdit(true);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Delete (optional later) */}

                <Tooltip title="Delete Project">
                  <IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Grid sx={{ mt: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            m: 2,
          }}
        >
          {/* Left side: Filters */}
          {isSmallScreen ? (
            <FilterModal
              ifProjectNameFilter
              ifPriorityFilter
              setQuary={setQuery}
              handleRefresh={refreshFunc}
              queryParam={query.assigned_to === "unassigned" ? query : query}
              unassigned={hiddenButton === "unassigned"}
            />
          ) : (
            <DataFilter
              ifProjectNameFilter
              ifPriorityFilter
              setQuary={setQuery}
              handleRefresh={refreshFunc}
              queryParam={query.assigned_to === "unassigned" ? query : query}
              unassigned={hiddenButton === "unassigned"}
            />
          )}

          {/* Right side: Create Project */}
          {user.role === "Admin" && <CreateProject refresh={refreshFunc} />}
        </Box>

        <ApiPaginateSearch
          apiEnd={ApiEndpoints.GET_PROJECTS}
          columns={columns}
          apiData={apiData}
          setApiData={setApiData}
          tableStyle={CustomStyles}
          queryParam={query ? query : ""}
          returnRefetch={(ref) => {
            refreshRef.current = ref;
          }}
        />
        <EditProject
          open={openEdit}
          data={editData}
          onClose={() => setOpenEdit(false)}
          refresh={refreshFunc}
        />
        <DeleteProjectModel
          open={openDeleteModal}
          handleClose={() => setOpenDeleteModal(false)}
          selectedProject={deleteData}
          refresh={refreshFunc}
        />
      </Grid>
      <Modal open={openImageModal}
       onClose={() => setOpenImageModal(false)}
       >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 320 },
            height: { xs: 260, sm: 300 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden", // ✅ IMPORTANT
            }}
          >
            <img
              src={selectedImage}
              alt="Project"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain", // ✅ KEY
                borderRadius: 8,
              }}
            />
          </Box>

          <Button
            size="small"
            variant="contained"
            sx={{ mt: 1 }}
            onClick={() => setOpenImageModal(false)}
          >
            Close
          </Button>
        </Box>
      </Modal>
  
    </>
  );
};

export default TableProjects;
